import Vue from 'vue';
import {
    BetlerBonus,
    BonusKind,
} from '@core/models/bonus/Bonus';
import {
    Promotion,
    PromotionBonusType,
    PromotionQualification,
    PromotionQualificationType,
    PromotionStatus,
} from '../types/Promotion';
import {
    BetlerPromotionDto,
    BetlerPromotionStatusDto,
    QualificationCriteriaDto,
    QualificationCriteriaType,
    BetlerQualificationProgressDto,
    BetlerMultiConditionQualificationProgressDto,
    stakeMultiplierString,
    BetlerCampaignImage,
    CampaignQualificationType,
} from '../types/dtos/BetlerPromotionDto';
import {
    areFieldsSet,
    logger,
} from '@superbet-group/web.lib.utilities';
import ProductTypeDto from '../../bonuses/types/betlerDto/ProductTypeDto';
import { transformBetlerBonus } from '../../bonuses/mappers/transformBetlerBonus';
import { BonusType } from '@superbet-group/web.types';
import { BonusConditions } from '@core/models/bonus/BonusConditions';
import { Condition } from '../../shared/types/Condition';
import {
    BonusRequirement,
    BonusRequirementKey,
} from '../../bonuses/utils/types';
import { SharedCustomContentConditions } from '../../shared/types/CustomContent';
import { orderBy as _orderBy } from 'lodash';
import { getTermsAndConditionsUrl } from '../utils/helpers';
import { getBonusRequirements } from '../../bonuses/utils/bonusHelpers';
import { isCallToActionTextValid } from '@src/app/promotionHub/shared/utils/helpers';
import { PromoHubActionButton } from '../../shared/types/PromoHubActionButton';
import { isValidUrl } from '@src/app/utils/domainHelpers';

export function transformBetlerPromotion(dto: BetlerPromotionDto): Promotion | null {
    if (!dto.campaign.bonus) {
        return null;
    }

    const betlerBonus = transformBetlerBonus(dto.campaign.bonus) as BetlerBonus;
    const isMultiConditionPromotion = dto.qualification_progress && Object.prototype.hasOwnProperty.call(dto.qualification_progress, 'current_phase');

    return {
        kind: BonusKind.Betler,
        title: dto.campaign.name,
        promotionId: dto.campaign.campaign_id,
        promotionInstanceId: dto.campaign.campaign_instance_id,
        optInCode: dto.id,
        status: getPromotionStatus(dto.status),
        startDate: new Date(dto.campaign.dates.start),
        expirationDate: dto.campaign.dates.finish ? new Date(dto.campaign.dates.finish) : null,
        image: getPromotionImage(dto.campaign.bonus.product, dto.campaign.campaign_images),
        conditions: getPromotionConditions(dto),
        optInDate: new Date(dto.opt_in_out_date),
        bonus: {
            name: betlerBonus.name,
            bonusAmount: betlerBonus.rewardAmount,
            conditions: getBonusConditions(betlerBonus),
            type: getPromotionBonusType(betlerBonus.type),
            priority: betlerBonus.priority,
            rewardType: betlerBonus.rewardType,
        },
        promoActionButton: getPromotionActionButton(dto),
        isMultiConditionPromotion: isMultiConditionPromotion,
        isAdventCalendarPromotion: dto.campaign.internal_name.toLocaleLowerCase().includes('[adventcalendar]'),
        isManualPromotion: dto.campaign.qualification_type === CampaignQualificationType.Manual,
        ...areFieldsSet(dto, 'qualification_progress') && {
            qualificationProgress: isMultiConditionPromotion
                ? (dto.qualification_progress as BetlerMultiConditionQualificationProgressDto).phases.map((qualification) => transformQualificationProgress(qualification, true)).flat()
                : transformQualificationProgress(dto.qualification_progress as BetlerQualificationProgressDto),
        },
        termsAndConditionsUrl: getTermsAndConditionsUrl(dto.campaign.terms_and_conditions_url),
    };
}

function getPromotionStatus(dtoStatus: BetlerPromotionStatusDto): PromotionStatus {
    const promotionStatusMappings: Record<BetlerPromotionStatusDto, PromotionStatus> = {
        [BetlerPromotionStatusDto.Eligible]: PromotionStatus.Available,
        [BetlerPromotionStatusDto.OptedIn]: PromotionStatus.InProgress,
        [BetlerPromotionStatusDto.InProgress]: PromotionStatus.InProgress,
        [BetlerPromotionStatusDto.Qualified]: PromotionStatus.Completed,
        [BetlerPromotionStatusDto.Expired]: PromotionStatus.Expired,
    };
    const status = promotionStatusMappings[dtoStatus];
    if (!status) {
        logger.error(new Error(`Undefined category for bonus status: ${dtoStatus}`));
    }
    return promotionStatusMappings[dtoStatus];
}

function getPromotionImage(productType: ProductTypeDto, campaignImages?: BetlerCampaignImage[]): string {
    if (campaignImages && campaignImages[0]?.image_url) {
        return campaignImages[0].image_url;
    }
    const imageMapping: Record<ProductTypeDto, string> = {
        [ProductTypeDto.Sportsbook]: '/static/img/promotions/sport.png',
        [ProductTypeDto.Gaming]: '/static/img/promotions/casino.png',
        [ProductTypeDto.Virtuals]: '/static/img/promotions/casino.png',
        [ProductTypeDto.Lotto]: '/static/img/promotions/casino.png',
        [ProductTypeDto.Honey]: '/static/img/promotions/honey.png',
    };

    return imageMapping[productType];
}

function getPromotionBonusType(type: BonusType): PromotionBonusType | null {
    switch (type) {
        case BonusType.Casino:
        case BonusType.CasinoVirtuals:
        case BonusType.Cash:
        case BonusType.LiveDealer:
            return PromotionBonusType.Casino;
        case BonusType.Bingo:
        case BonusType.FreeSpins:
            return PromotionBonusType.External;
        case BonusType.Freebet:
            return PromotionBonusType.FreeBet;
        case BonusType.Sport:
            return PromotionBonusType.Sportsbook;
        case BonusType.HoneyToken:
            return PromotionBonusType.Honey;
        default:
            return null;
    }
}

function getBonusConditions(bonus: BetlerBonus): Condition[] {
    const bonusRequirementParams = {
        bonusConditions: bonus.bonusConditions || <BonusConditions>{},
        wageringRequirements: 0, // Betler needs to fix wagering requirement - bonus.wageringRequirements
        type: bonus.type,
        bonusSubtype: bonus.bonusSubtype,
    };
    const bonusRequirements = getBonusRequirements(bonusRequirementParams);

    return bonusRequirements.map((requirement: BonusRequirement) => {
        if (requirement.key !== BonusRequirementKey.ExpirationDate) {
            return {
                label: requirement.text,
                value: requirement.condition,
                isCurrency: requirement.isCurrency,
                type: SharedCustomContentConditions.sharedDefault,
            };
        }
    }).filter((item) => !!item) as Condition[];
}

function getPromotionConditions(promotion: BetlerPromotionDto): Condition[] {
    const conditionOrder = Object.values(QualificationCriteriaType);

    const conditions = promotion.campaign.qualification_criteria.map((qualification: QualificationCriteriaDto) => {
        if (qualification.criteria !== 'adventCalendarPromo') {
            return {
                label: qualification.criteria,
                type: SharedCustomContentConditions.sharedDefault,
                qualificationType: qualification.type,
            };
        }
    }).filter((item) => !!item)
        .sort((a, b) => conditionOrder.indexOf(a!.qualificationType) - conditionOrder.indexOf(b!.qualificationType)) as Condition[];

    if (
        promotion.campaign.qualification_type !== CampaignQualificationType.Manual
        && promotion.campaign.qualification_criteria.some((q) => q.type === QualificationCriteriaType.Sportsbook)
    ) {
        conditions.push({
            type: SharedCustomContentConditions.sharedBetsMustSettle,
            value: 'promotion.card.restriction.bets_must_settle',
        });
    }

    const promoEndDate = promotion.campaign.dates.finish ? new Date(promotion.campaign.dates.finish) : null;
    if (promoEndDate) {
        conditions.push({
            type: SharedCustomContentConditions.sharedExpirationDate,
            label: 'promotion.card.expires_on',
            value: `<b>${Vue.prototype.$formatDateTime(promoEndDate, 'ddmmyyyy')} - ${Vue.prototype.$formatTime(promoEndDate, 'HHMM')}h</b>`,
        });
    }

    return conditions;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function transformQualificationProgress(dto: BetlerQualificationProgressDto, isMultiCondition?: boolean): PromotionQualification[] {
    const qualificationProgress = [];

    if (dto.currency_amount_of_deposits_required !== undefined && dto.currency_amount_of_deposits_made !== undefined) {
        qualificationProgress.push({
            type: PromotionQualificationType.DepositMoney,
            amountRequired: dto.currency_amount_of_deposits_required,
            amountFulfilled: dto.currency_amount_of_deposits_made,
            isQualificationMet: dto.currency_amount_of_deposits_made >= dto.currency_amount_of_deposits_required,
            phase: dto.phase,
            title: 'promotion.card.details.deposit_amount',
            isCurrency: true,
        });
    }
    if (dto.no_of_deposits_required !== undefined && dto.no_of_deposits_made !== undefined) {
        qualificationProgress.push({
            type: PromotionQualificationType.DepositNumber,
            amountRequired: dto.no_of_deposits_required,
            amountFulfilled: dto.no_of_deposits_made,
            isQualificationMet: dto.no_of_deposits_made >= dto.no_of_deposits_required,
            phase: dto.phase,
            title: 'promotion.card.details.deposit_count',
        });
    }
    if (dto.currency_amount_of_bets_required !== undefined && dto.currency_amount_of_bets_made !== undefined) {
        const isStakeMultiplierVisible = dto.currency_amount_of_bets_required === stakeMultiplierString;
        const waitingForBetsToSettle = !isStakeMultiplierVisible
            && dto.currency_amount_of_unsettled_bets_made! > 0
            && (dto.currency_amount_of_bets_made as number) >= (dto.currency_amount_of_bets_required as number);

        qualificationProgress.push({
            type: PromotionQualificationType.WagerMoney,
            amountRequired: (!isStakeMultiplierVisible ? dto.currency_amount_of_bets_required : 0) as number,
            amountFulfilled: dto.currency_amount_of_bets_made,
            isQualificationMet: !isStakeMultiplierVisible && dto.currency_amount_of_bets_made >= (dto.currency_amount_of_bets_required as number),
            phase: dto.phase,
            title: isStakeMultiplierVisible ? 'promotion.card.details.wager_deposit' : 'promotion.card.details.wager_sport_bets',
            isCurrency: true,
            stakeMultiplier: isStakeMultiplierVisible ? dto.total_staked_multiplier : 0,
            waitingForBetsToSettle,
        });
    }
    if (dto.no_of_bets_required !== undefined && dto.no_of_bets_made !== undefined) {
        qualificationProgress.push({
            type: PromotionQualificationType.WagerBets,
            amountRequired: dto.no_of_bets_required,
            amountFulfilled: dto.no_of_bets_made,
            isQualificationMet: dto.no_of_bets_made >= dto.no_of_bets_required,
            phase: dto.phase,
            title: 'promotion.card.details.place_sport_bets',
        });
    }
    if (dto.currency_amount_of_spins_required !== undefined && dto.currency_amount_of_spins_made !== undefined) {
        qualificationProgress.push({
            type: PromotionQualificationType.CasinoMoney,
            amountRequired: dto.currency_amount_of_spins_required,
            amountFulfilled: dto.currency_amount_of_spins_made,
            isQualificationMet: dto.currency_amount_of_spins_made >= dto.currency_amount_of_spins_required,
            phase: dto.phase,
            title: 'promotion.card.details.wager_casino_games',
            isCurrency: true,
        });
    }
    if (dto.no_of_spins_required !== undefined && dto.no_of_spins_made !== undefined) {
        qualificationProgress.push({
            type: PromotionQualificationType.CasinoGames,
            amountRequired: dto.no_of_spins_required,
            amountFulfilled: dto.no_of_spins_made,
            isQualificationMet: dto.no_of_spins_made >= dto.no_of_spins_required,
            phase: dto.phase,
            title: 'promotion.card.details.play_casino_games',
        });
    }

    if (isMultiCondition) {
        return _orderBy(qualificationProgress, 'phase');
    } else {
        const qualificationOrder = Object.keys(PromotionQualificationType);
        return qualificationProgress.sort((a, b) => qualificationOrder.indexOf(a.type) - qualificationOrder.indexOf(b.type));
    }
}

function getPromotionActionButton(dto: BetlerPromotionDto): PromoHubActionButton {
    return {
        text: dto.campaign.call_to_action_text && isCallToActionTextValid(dto.campaign.call_to_action_text) ? dto.campaign.call_to_action_text : undefined,
        link: dto.campaign.call_to_action_deeplink && isValidUrl(dto.campaign.call_to_action_deeplink) ? dto.campaign.call_to_action_deeplink : undefined,
    };
}
